// Analytics/services/endpoints/finaces.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_FINANCES === 'true' || true,
    endpoints: {
        'total-billing': true,
        'total-discount': true,
        'total-income': true,
        'billing-by-location': true,
        'billing-by-insurance': true,
        'billing-by-department': true,
        'monthly-billing-trends': true,
        'outpatient-billing-trends': true,
        'category-billing-trends': true,
        'patient-category-billing': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockTotalBillingResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [45000, 52000, 48000, 55000, 51000, 58000, 60000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 369000
    }
});

const getMockTotalDiscountResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Discounts", data: [4500, 5200, 4800, 5500, 5100, 5800, 6000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 36900
    }
});

const getMockTotalIncomeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Income", data: [40500, 46800, 43200, 49500, 45900, 52200, 54000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 332100
    }
});

const getMockBillingByLocationResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [150000, 120000, 99000] }
        ],
        categories: ["Main Hospital", "Emergency", "Outpatient"],
        count: 369000
    }
});

const getMockBillingByInsuranceResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [184500, 110700, 73800] }
        ],
        categories: ["Private Insurance", "Public Insurance", "Self-Pay"],
        count: 369000
    }
});

const getMockBillingByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [120000, 80000, 60000, 50000, 59000] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics", "Other"],
        count: 369000
    }
});

const getMockMonthlyBillingTrendsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Total Billing", data: [45000, 52000, 48000, 55000, 51000, 58000, 60000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 369000
    }
});

const getMockOutpatientBillingTrendsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Outpatient Billing", data: [30000, 35000, 32000, 38000, 34000, 40000, 42000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 251000
    }
});

const getMockCategoryBillingTrendsResponse = () => ({
    status: 200,
    stats: {
        lines: [
            { category: "Consultation", total: 120000, count: 500 },
            { category: "Laboratory", total: 80000, count: 300 },
            { category: "Radiology", total: 60000, count: 200 },
            { category: "Pharmacy", total: 50000, count: 400 },
            { category: "Other", total: 59000, count: 250 }
        ]
    }
});

const getMockPatientCategoryBillingResponse = () => ({
    status: 200,
    stats: {
        lines: [
            {
                totalPaidInPeriod: 125000,
                billItemId: 1,
                billReference: "BILL-001",
                billUuid: "bill-uuid-1",
                category: "Consultation",
                concept: "General Consultation",
                department: "Cardiology",
                departmentUuid: "dept-uuid-1",
                latestPaymentDate: "2024-01-15T10:30:00Z",
                patient: "John Doe",
                patientInvestigationUuid: "patient-inv-uuid-1",
                practitioner: "Dr. Smith",
                service: "Outpatient"
            },
            {
                totalPaidInPeriod: 98000,
                billItemId: 2,
                billReference: "BILL-002",
                billUuid: "bill-uuid-2",
                category: "Laboratory",
                concept: "Blood Test",
                department: "Laboratory",
                departmentUuid: "dept-uuid-2",
                latestPaymentDate: "2024-01-16T14:20:00Z",
                patient: "Jane Smith",
                patientInvestigationUuid: "patient-inv-uuid-2",
                practitioner: "Dr. Jones",
                service: "Outpatient"
            },
            {
                totalPaidInPeriod: 75000,
                billItemId: 3,
                billReference: "BILL-003",
                billUuid: "bill-uuid-3",
                category: "Radiology",
                concept: "X-Ray",
                department: "Radiology",
                departmentUuid: "dept-uuid-3",
                latestPaymentDate: "2024-01-17T09:15:00Z",
                patient: "Bob Johnson",
                patientInvestigationUuid: "patient-inv-uuid-3",
                practitioner: "Dr. Williams",
                service: "Inpatient"
            },
            {
                totalPaidInPeriod: 43000,
                billItemId: 4,
                billReference: "BILL-004",
                billUuid: "bill-uuid-4",
                category: "Pharmacy",
                concept: "Medication",
                department: "Pharmacy",
                departmentUuid: "dept-uuid-4",
                latestPaymentDate: "2024-01-18T11:45:00Z",
                patient: "Alice Brown",
                patientInvestigationUuid: "patient-inv-uuid-4",
                practitioner: "Dr. Davis",
                service: "Outpatient"
            },
            {
                totalPaidInPeriod: 28000,
                billItemId: 5,
                billReference: "BILL-005",
                billUuid: "bill-uuid-5",
                category: "Consultation",
                concept: "Follow-up",
                department: "Neurology",
                departmentUuid: "dept-uuid-5",
                latestPaymentDate: "2024-01-19T16:00:00Z",
                patient: "Charlie Wilson",
                patientInvestigationUuid: "patient-inv-uuid-5",
                practitioner: "Dr. Miller",
                service: "Outpatient"
            }
        ]
    }
});

// Mock registry
const MOCK_REGISTRY = {
    'total-billing': getMockTotalBillingResponse,
    'total-discount': getMockTotalDiscountResponse,
    'total-income': getMockTotalIncomeResponse,
    'billing-by-location': getMockBillingByLocationResponse,
    'billing-by-insurance': getMockBillingByInsuranceResponse,
    'billing-by-department': getMockBillingByDepartmentResponse,
    'monthly-billing-trends': getMockMonthlyBillingTrendsResponse,
    'outpatient-billing-trends': getMockOutpatientBillingTrendsResponse,
    'category-billing-trends': getMockCategoryBillingTrendsResponse,
    'patient-category-billing': getMockPatientCategoryBillingResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/finances`;
    const endpointPath = endpoint || '';
    console.log('Building URL:', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeRequest = async (params) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (MOCK_CONFIG.enabled && MOCK_CONFIG.endpoints[endpointPath] && MOCK_REGISTRY[endpointPath]) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, getAuthHeaders());
        if (response.status === 200) {
            console.log('Finances API Response:', endpointPath, response.data);
            return response.data;
        }
        return response.data;
    } catch (error) {
        console.error('Error fetching finances data:', error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating finances endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate) => 
        makeRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        })
    );
};

export const financesServices = {
    getTotalBilling: createEndpoint('total-billing'),
    getTotalDiscount: createEndpoint('total-discount'), 
    getTotalIncome: createEndpoint('total-income'),
    getBillingByLocation: createEndpoint('billing-by-location'),
    getBillingByInsurance: createEndpoint('billing-by-insurance'),
    getBillingByDepartment: createEndpoint('billing-by-department'),
    getMonthlyBillingTrends: createEndpoint('monthly-billing-trends'),
    getOutpatientBillingTrends: createEndpoint('outpatient-billing-trends'),
    getCategoryBillingTrends: createEndpoint('category-billing-trends'),
    getPatientCategoryBilling: createEndpoint('patient-category-billing'),
};

export const financesEndpoints = {
    TOTAL_BILLING: '/total-billing',
    TOTAL_DISCOUNT: '/total-discount',
    TOTAL_INCOME: '/total-income',
    BILLING_BY_LOCATION: '/billing-by-location',
    BILLING_BY_INSURANCE: '/billing-by-insurance',
    BILLING_BY_DEPARTMENT: '/billing-by-department',
    BILLING_HISTORICAL: '/billing-historical',
    OUTPATIENTS_BILLING: '/outpatients-billing',
    INPATIENTS_BILLING: '/inpatients-billing'
};
